import { m, type Message, type PlainKey } from '@/i18n';
import type { LaneAttribute } from './lane-attributes';
import type { PendingStimulus } from './resolution/types';
import { holdsRequesterSeat, holdsSeat, isOnlyParty, type Actor, type SeatedItem } from './seats';
import { lifecycleReading, type LifecycleReading } from './tags';
import { domainError, type DomainError } from './types';

/**
 * **FINISHING AN ITEM: ONE LIFECYCLE, ONE ACT PER EDGE** (Leon, 2026-11-03,
 * docs/CLOSED_AXIS.md "Finishing"). The one table every surface reads to ask
 * "which acts on the item's ending are open to this viewer here, and which of
 * them is the next step".
 *
 * Four POSITIONS, each a reading over two atoms (`lifecycleReading`):
 *
 * | position  | delivered | closed |
 * | --------- | --------- | ------ |
 * | open      | no        | no     |
 * | review    | yes       | no     |
 * | done      | yes       | yes    |
 * | cancelled | no        | yes    |
 *
 * Six EDGES, one word each. What each edge DOES (its plan) is the resolver's
 * (`resolveEdge`, src/domain/resolution/intent.ts); this table says only
 * which edges stand here and for whom:
 *
 * | edge             | from               | to        | for                  |
 * | ---------------- | ------------------ | --------- | -------------------- |
 * | Done             | open, no duty      | done      | anyone with standing |
 * | Ready for review | open               | review    | anyone with standing |
 * | Accept           | review             | done      | the reviewer         |
 * | Deny             | review             | open      | the reviewer         |
 * | Reopen           | review, done, canc | open      | anyone with standing |
 * | Cancel           | open, review       | cancelled | the requester seat   |
 *
 * The FORWARD edge of a position is what the item head's pill and the dock's
 * pill push in one tap, and what a tick means: open without a duty is Done,
 * open with a duty is Ready for review, review for the reviewer is Accept,
 * review for anyone else has none, and done or cancelled is Reopen, in the
 * quiet register (the outline, not the accent fill: Leon, 2026-11-03).
 *
 * Done is NOT offered while a review duty stands, not even off the pill: the
 * way around a duty is to remove it first (Leon, 2026-09-21). Ready for
 * review as a plain row is absent where the viewer is the only party (nobody
 * to review), and where a flow's stage move hands the work in.
 *
 * Every surface reads this table: the act sheet and the pill
 * (`domain/act-sheet.ts`), the state line's position menu and its "+"
 * (`domain/state-line.ts`), the attention strip and the board card's review
 * line (`reviewAskOf`), the checklist rail's tick, the board lanes and the
 * agent's `item_act` verbs (verb = edge). The dry run still prices every row
 * (enabled, lane, reason); the table never decides a right.
 */

/** The four positions: the readings over the delivered and closed atoms. */
export type Position = LifecycleReading;

export const POSITIONS: readonly Position[] = ['open', 'review', 'done', 'cancelled'];

export const EDGE_KINDS = ['done', 'ready', 'accept', 'deny', 'reopen', 'cancel'] as const;
export type EdgeKind = (typeof EDGE_KINDS)[number];

/** One word per edge. A row and the pill that leads it read the same key. */
export const EDGE_VERB: Record<EdgeKind, PlainKey> = {
  done: 'acts.verb.done',
  ready: 'acts.verb.readyForReview',
  accept: 'acts.verb.accept',
  deny: 'acts.verb.deny',
  reopen: 'acts.verb.reopen',
  cancel: 'acts.verb.cancel',
};

/** Where each edge lands. */
export const EDGE_TARGET: Record<EdgeKind, Position> = {
  done: 'done',
  ready: 'review',
  accept: 'done',
  deny: 'open',
  reopen: 'open',
  cancel: 'cancelled',
};

/** Who an edge is for. A viewer the edge is not for sees it LOCKED where the
 *  surface shows it at all (the Cancel row, as since 2026-09-20), or not at
 *  all (Accept and Deny, which stand only for the reviewer). */
export type EdgeAudience = 'anyone' | 'reviewer' | 'requester';

/** The facts the table is a function of. Pure values, no context. */
export interface EdgeFacts {
  position: Position;
  /** The viewer holds the review: the requester seat, or the person (or a
   *  group of theirs) a flow's review stage names as its gatekeeper. */
  reviewer: boolean;
  /** The viewer holds the requester seat: the Cancel row's seat. */
  requester: boolean;
  /** The viewer is the only party on the item and no seat is offered:
   *  nobody else to review, so Ready for review is no row of its own. */
  onlyParty: boolean;
  /** A review duty stands: the item's own setting, or a flow stage that
   *  means `verdict: accepted`. */
  reviewRequired: boolean;
  /** A flow's stage `means` apply `delivered`: the stage move hands the work
   *  in, so Ready for review is no row of its own. */
  deliveryCovered: boolean;
}

export interface Edge {
  kind: EdgeKind;
  to: Position;
  verb: PlainKey;
  /** What the edge sends through the resolver. The forward edge of open and
   *  of review sends the TICK ("I am done"), so the pill, the dock and a
   *  checklist's box are one gesture and an un-tick takes it back; every other
   *  edge sends its own stimulus. */
  stimulus: PendingStimulus;
  /** The next step: the pill's word. One per position at most. */
  forward: boolean;
  /** The danger register: the way down (Cancel). */
  danger: boolean;
  /** The quiet register: the way back as the pill (Reopen on a done or
   *  cancelled item, Leon 2026-11-03). The accent fill belongs to a step
   *  forward, and a concluded item has none, so its one act wears the
   *  outline. */
  quiet: boolean;
  for: EdgeAudience;
  /** The viewer is not who the edge is for: a locked row wearing the seat. */
  locked: boolean;
}

const TICK: PendingStimulus = { kind: 'tick' };
const edgeStimulus = (edge: Exclude<EdgeKind, 'cancel'>): PendingStimulus => ({
  kind: 'edge',
  edge,
});

function edge(
  kind: EdgeKind,
  f: EdgeFacts,
  opts: { forward?: boolean; for?: EdgeAudience } = {},
): Edge {
  const forward = opts.forward ?? false;
  const audience = opts.for ?? 'anyone';
  const stimulus: PendingStimulus =
    kind === 'cancel'
      ? { kind: 'cancel' }
      : forward && kind !== 'reopen'
        ? TICK
        : edgeStimulus(kind);
  return {
    kind,
    to: EDGE_TARGET[kind],
    verb: EDGE_VERB[kind],
    stimulus,
    forward,
    danger: kind === 'cancel',
    quiet: kind === 'reopen' && forward,
    for: audience,
    locked: audience === 'requester' ? !f.requester : false,
  };
}

/**
 * **THE TABLE**: the edges standing at this position for this viewer, the
 * forward edge first. Pure, total over its facts.
 */
export function lifecycleEdges(f: EdgeFacts): Edge[] {
  switch (f.position) {
    case 'open': {
      const out: Edge[] = [];
      if (f.reviewRequired) {
        out.push(edge('ready', f, { forward: true }));
      } else {
        out.push(edge('done', f, { forward: true }));
        if (!f.onlyParty && !f.deliveryCovered) out.push(edge('ready', f));
      }
      out.push(edge('cancel', f, { for: 'requester' }));
      return out;
    }
    case 'review':
      return [
        ...(f.reviewer
          ? [
              edge('accept', f, { forward: true, for: 'reviewer' }),
              edge('deny', f, { for: 'reviewer' }),
            ]
          : []),
        edge('reopen', f),
        edge('cancel', f, { for: 'requester' }),
      ];
    case 'done':
    case 'cancelled':
      return [edge('reopen', f, { forward: true })];
  }
}

/** The forward edge, or null where the viewer has none here. */
export function forwardEdge(edges: readonly Edge[]): Edge | null {
  return edges.find((e) => e.forward) ?? null;
}

/** What `edgeFactsFor` needs beyond the actor and the item. */
export interface EdgeContextFacts {
  reviewRequired: boolean;
  /** The refs a flow's review stage names as its gatekeeper. */
  reviewers?: readonly string[];
  /** A seat on the item is offered to someone. */
  seatOffered?: boolean;
  /** A flow's stage `means` apply `delivered`. */
  deliveryCovered?: boolean;
}

/** The table's facts for one viewer on one item. Pure. */
export function edgeFactsFor(
  actor: Actor,
  item: SeatedItem & { delivered: boolean; closed: boolean },
  facts: EdgeContextFacts,
): EdgeFacts {
  const requester = holdsRequesterSeat(actor, item);
  return {
    position: lifecycleReading(item),
    reviewer: requester || holdsSeat(actor, facts.reviewers ?? []),
    requester,
    onlyParty: isOnlyParty(actor, item) && !(facts.seatOffered ?? false),
    reviewRequired: facts.reviewRequired,
    deliveryCovered: facts.deliveryCovered ?? false,
  };
}

/** The edges for one viewer on one item: the table over `edgeFactsFor`. */
export function edgesFor(
  actor: Actor,
  item: SeatedItem & { delivered: boolean; closed: boolean },
  facts: EdgeContextFacts,
): Edge[] {
  return lifecycleEdges(edgeFactsFor(actor, item, facts));
}

/**
 * **What a Review item asks of this viewer** (the attention strip, the board
 * card's review line): their Accept, where the forward edge here is theirs.
 * Retires `deliveryAsk` (2026-11-03): the review is the one ask, and the
 * close a delivery once asked of its requester is the Done edge the doer
 * takes themselves.
 */
export function reviewAskOf(edges: readonly Edge[]): boolean {
  return forwardEdge(edges)?.kind === 'accept';
}

/**
 * The edge a gesture on a VALUE stands for: a lane drop, a position chip's
 * menu row. The conclusion's three endings and the completion axis map onto
 * the edges that land there; the verdict is a record, never an axis of its
 * own (2026-11-03), so its values map onto the review's two acts and its
 * `none` onto nothing. Null for a value no edge reaches.
 */
export function laneEdge(
  attribute: Exclude<LaneAttribute, 'active'>,
  value: string,
  edges: readonly Edge[],
): EdgeKind | null {
  const has = (kind: EdgeKind) => edges.some((e) => e.kind === kind);
  const first = (...kinds: EdgeKind[]) => kinds.find(has) ?? kinds[0] ?? null;
  switch (attribute) {
    case 'closed':
      return value === 'no'
        ? 'reopen'
        : value === 'yes'
          ? first('done', 'accept')
          : value === 'cancelled'
            ? 'cancel'
            : null;
    case 'delivered':
      return value === 'yes' ? first('ready', 'done') : value === 'no' ? 'reopen' : null;
    case 'verdict':
      return value === 'accepted' ? 'accept' : value === 'disputed' ? 'deny' : null;
  }
}

/**
 * Why an edge does not stand here for this viewer, in the words a menu row,
 * a refusal and an agent all read. The table decides THAT; this names WHY.
 */
export function edgeRefusal(kind: EdgeKind, f: EdgeFacts): DomainError {
  const at = f.position;
  if (at === 'done' || at === 'cancelled') {
    return kind === 'reopen'
      ? domainError('no_change', m('resolution.refusal.alreadyOpen'))
      : domainError('no_change', m('resolution.refusal.alreadyEnded'));
  }
  switch (kind) {
    case 'done':
      return at === 'review'
        ? domainError('invalid_state', m('resolution.refusal.inReview'))
        : domainError('forbidden', m('resolution.refusal.reviewRequired'));
    case 'ready':
      return at === 'review'
        ? domainError('no_change', m('resolution.refusal.alreadyInReview'))
        : f.onlyParty
          ? domainError('invalid_state', m('resolution.refusal.nobodyToReview'))
          : domainError('invalid_state', m('resolution.refusal.stageHandsIn'));
    case 'accept':
    case 'deny':
      return at === 'open'
        ? domainError('invalid_state', m('resolution.refusal.notInReview'))
        : domainError('forbidden', m('resolution.refusal.reviewersAct'));
    case 'reopen':
      return domainError('no_change', m('resolution.refusal.alreadyOpen'));
    case 'cancel':
      return domainError('no_change', m('resolution.refusal.alreadyEnded'));
  }
}

/** The position's word, for a menu row naming where an edge lands. */
export const POSITION_WORD: Record<Position, Message> = {
  open: m('attributes.value.closed.no'),
  review: m('itemActions.stateChip.review'),
  done: m('attributes.value.closed.yes'),
  cancelled: m('attributes.value.closed.cancelled'),
};
